import { StyleSheet, css } from 'aphrodite';
import * as React from 'react';
import { Button } from '../../../../web/components-v2/Button';
import { ConfirmTakeOverDialog } from '../../ConfirmTakeOverDialog';
import { THEME } from '../../../../web/components-v2/Theme/theme';
import { styleSheet } from '../styles';

interface IProps {
	/** The tab holding the dialer is on a call, so moving it here would end that call. */
	isBlocked: boolean;
	onTakeOver: () => Promise<boolean> | void;
}

/**
 * Shown in the status banner on a tab the dialer does not live in.
 *
 * Deliberately inline rather than reusing `SplashScreen`: that covers the viewport, so a
 * second tab would have the whole queue hidden behind a prompt whose only action is to
 * take the dialer off the tab the rep is working in. A rep who opened another tab to look
 * something up should get the page.
 */
export const DialerElsewhere: React.FC<IProps> = ({ isBlocked, onTakeOver }) => {
	const [moving, setMoving] = React.useState(false);
	const [confirming, setConfirming] = React.useState(false);

	const move = async () => {
		setConfirming(false);
		setMoving(true);
		// The claim waits out the cross-tab veto window before the token fetch even starts.
		const moved = await onTakeOver();
		if (!moved) setMoving(false);
	};

	// Taking the dialer off a tab that is on a call ends that call, so ask first.
	const handleClick = () => {
		if (isBlocked) {
			setConfirming(true);
			return;
		}
		void move();
	};

	return (
		<div className={css(styles.row)}>
			<div className={css(styleSheet.dialerStatus, styles.label)}>
				<div
					className={css(
						styleSheet.dialerCallInfoText,
						styleSheet.dialerStatusDot,
						styleSheet.dialerStatusDotRegistering
					)}
				/>
				<div className={css(styleSheet.dialerCallInfoText)}>
					{isBlocked ? 'On a call in another tab' : 'Dialer is in another tab'}
				</div>
			</div>
			<span className={css(styles.action)}>
				<Button variant='primary' size='sm' isLoading={moving} onClick={handleClick}>
					Move here
				</Button>
			</span>
			<ConfirmTakeOverDialog open={confirming} onOpenChange={setConfirming} onConfirm={() => void move()} />
		</div>
	);
};

const styles = StyleSheet.create({
	row: {
		alignItems: 'center',
		display: 'flex',
		flex: 1,
		gap: THEME.spacing.sm,
	},
	// The banner allows 330px, so `min-width: 0` lets the label ellipsis instead of pushing
	// the action out of the row.
	label: {
		minWidth: 0,
	},
	action: {
		flexShrink: 0,
	},
});
